import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  Banner, Button, Card, Checkbox, DataTable, Details, Dialog, Select, Skeleton, StatusChip,
  TextField, TopAppBar, useSnackbar,
} from '@meshble/molo';
import { api, must, mustOk, type components } from '../lib/api';
import { useIstanze, useServers } from '../lib/dati';
import { durata, quandoRelativo } from '../lib/attivita';
import { BarraUtente } from '../shell';
import { Copy, Shield, Warn } from '../icons';
import { Anello, CaricoSezione, Logo } from '../brand';
import { localeIntl, t } from '../lib/lingua';
import { MACCHINA_VUOTA, ScegliMacchina, useCredenzialiCloud, type MacchinaCloud } from './server-cloud';
import { ConPermesso } from '../permesso';
import { usePermessi } from '../session';

type Controllo = components['schemas']['Controllo'];
type Run = components['schemas']['Run'];

/**
 * La continuità operativa: siamo pronti?, è successo qualcosa, le
 * esercitazioni.
 *
 * I pezzi del ripristino d'emergenza c'erano già — la copertura in Backup,
 * lo scollegamento nella scheda del server, il ripristino su un altro server
 * nel dialogo di un'istanza — ma chi ha eseguito la prima esercitazione ha
 * dovuto saperli a memoria. Qui stanno in un posto solo, con i semafori
 * misurati contro gli obiettivi promessi al cliente e lo scenario «server
 * perso» che si esegue con un click e si cronometra da solo.
 */

/** Il colore di un semaforo, e la parola. */
const SEMAFORO: Record<string, { tone: 'success' | 'error' | 'neutral' | 'running'; label: string }> = {
  ok: { tone: 'success', label: 'A posto' },
  warn: { tone: 'running', label: 'Da guardare' },
  fail: { tone: 'error', label: 'Non regge' },
  unknown: { tone: 'neutral', label: 'Non si sa' },
};

const FASE: Record<string, string> = {
  provisioning: 'Sto creando e installando il server nuovo',
  decommissioning: 'Sto scollegando il server perso',
  restoring: 'Sto facendo rinascere le istanze',
  done: 'Finita',
};

const ESITO_CORSA: Record<string, { label: string; tone: 'success' | 'error' | 'running' }> = {
  running: { label: 'In corso', tone: 'running' },
  succeeded: { label: 'Riuscita', tone: 'success' },
  failed: { label: 'Non riuscita', tone: 'error' },
};

const ESITO_RIGA: Record<string, { label: string; tone: 'success' | 'error' | 'running' | 'neutral' }> = {
  pending: { label: 'In attesa', tone: 'neutral' },
  restoring: { label: 'In ripristino', tone: 'running' },
  succeeded: { label: 'Risponde', tone: 'success' },
  failed: { label: 'Non tornata', tone: 'error' },
  skipped: { label: 'Senza backup', tone: 'error' },
};

/** «4 h», «1 h 30 min», «20 min»: gli obiettivi si leggono così. */
function ore(minuti: number): string {
  return durata(minuti * 60);
}

export function ContinuitaPage() {
  const { slug } = useParams({ from: '/shell/orgs/$slug/continuita' });
  const navigate = useNavigate();
  const qc = useQueryClient();
  const avvisa = useSnackbar();
  const [obiettivi, setObiettivi] = useState(false);
  const [disastro, setDisastro] = useState(false);

  const q = useQuery({
    queryKey: ['continuity', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/continuity', { params: { path: { slug } } })),
    refetchInterval: 60_000,
  });
  const corse = useQuery({
    queryKey: ['continuity-runs', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/continuity/runs', { params: { path: { slug } } })),
    // Finché una corsa è in corso la tavola si aggiorna da sola: chi l'ha
    // dichiarata sta guardando qui.
    refetchInterval: (r) => ((r.state.data?.runs ?? []).some((c) => c.status === 'running') ? 5000 : false),
  });
  const p = q.data;
  const inCorso = (corse.data?.runs ?? []).find((c) => c.status === 'running');

  const copiaRunbook = () => {
    if (!p) return;
    void navigator.clipboard?.writeText(p.runbook);
    avvisa(t('Runbook copiato: incollalo dove lo ritrovi quando l’hub non c’è'));
  };

  return (
    <>
      <TopAppBar
        title={t('Continuità')}
        actions={
          <>
            <Button variant="filled" icon={<Warn />} onClick={() => setDisastro(true)}>
              {t('Il server è perso')}
            </Button>
            <BarraUtente slug={slug} />
          </>
        }
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        <div className="md-stack" style={{ gap: 'var(--md-sys-spacing-5)' }}>
          {q.error && <Banner tone="error" title={t('Non riesco a leggere la prontezza')}>{q.error.message}</Banner>}

          {inCorso && (
            <Card variant="outlined">
              <div className="md-attesa">
                <Logo size={56} anim="livello" label={t('Corsa in corso')} />
                <p className="md-body-medium">
                  {t('{cosa} per {perso} → {destinazione}: {fase}.', {
                    cosa: <b>{inCorso.exercise ? t('Esercitazione in corso') : t('Ripristino in corso')}</b>,
                    perso: inCorso.lostServerName,
                    destinazione: inCorso.destServerName ?? '…',
                    fase: t(FASE[inCorso.phase] ?? inCorso.phase),
                  })}{' '}
                  {t('{fatte} di {totale} istanze rispondono.', { fatte: inCorso.itemsDone, totale: inCorso.itemsTotal })}
                </p>
                <Button variant="tonal" onClick={() => navigate({ to: '/orgs/$slug/continuita/$id', params: { slug, id: inCorso.id } })}>{t('Segui')}</Button>
              </div>
            </Card>
          )}

          {/* --- Siamo pronti? ------------------------------------------ */}
          <section className="md-stack">
            <div className="md-section-head">
              <h2 className="md-title-medium">{t('Siamo pronti?')}</h2>
              {p && (
                <Button variant="text" onClick={() => setObiettivi(true)}>
                  {t('Obiettivi: RTO {rto} · RPO {rpo} · hub {hub}', {
                    rto: ore(p.targets.rtoMinutes),
                    rpo: ore(p.targets.rpoMinutes),
                    hub: ore(p.targets.hubRtoMinutes),
                  })}
                </Button>
              )}
            </div>
            {q.isPending && <Skeleton height="12rem" />}
            {p && (
              <DataTable
                columns={[
                  { key: 'stato', header: t('Stato') },
                  { key: 'cosa', header: t('Cosa') },
                  { key: 'azione', header: '' },
                ]}
                rowKey={(r) => String(r.id)}
                rows={(p.checks ?? []).map((c: Controllo) => {
                  const s = SEMAFORO[c.state] ?? SEMAFORO.unknown!;
                  return {
                    id: c.key,
                    stato: <StatusChip status={c.state} label={t(s.label)} tone={s.tone} />,
                    cosa: (
                      <div className="md-due-righe">
                        <span className="md-body-large">{c.title}</span>
                        <span>{c.detail}</span>
                      </div>
                    ),
                    azione: c.link && c.link !== '/continuita' ? (
                      <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
                        <Button variant="text" onClick={() => navigate({ to: `/orgs/${slug}${c.link}` })}>{t('Vai')}</Button>
                      </div>
                    ) : null,
                  };
                })}
              />
            )}
          </section>

          {/* --- È successo qualcosa ------------------------------------ */}
          <section className="md-stack">
            <h2 className="md-title-medium">{t('È successo qualcosa')}</h2>
            <div className="md-metric-grid">
              <Card variant="outlined">
                <h3 className="md-title-small">{t('Un server è perso')}</h3>
                <p className="md-body-medium">
                  {t('Le sue istanze rinascono dall’ultimo backup su un altro server, {stesso}. Il server perso viene scollegato, poi un lavoro fa tutto il resto e cronometra.', {
                    stesso: <b>{t('con lo stesso nome e lo stesso indirizzo')}</b>,
                  })}
                </p>
                <Button variant="tonal" icon={<Warn />} onClick={() => setDisastro(true)}>{t('Il server è perso')}</Button>
              </Card>
              <Card variant="outlined">
                <h3 className="md-title-small">{t('Un’istanza è danneggiata')}</h3>
                <p className="md-body-medium">
                  {t('Si riportano i dati a un backup nella stessa istanza (con una copia di sicurezza prima), o si fa una copia accanto per guardare senza toccare.')}
                </p>
                <Button variant="tonal" onClick={() => navigate({ to: '/orgs/$slug/backup', params: { slug } })}>{t('Vai ai backup')}</Button>
              </Card>
              {/* Il runbook ha l'IP da cui l'hub entra in tutti i server e dove stanno i
                  suoi backup: è dello staff, e agli altri arriva vuoto (blocco 28.5). */}
              {p?.runbook && (
                <Card variant="outlined">
                  <h3 className="md-title-small">{t('L’hub è perso')}</h3>
                  <p className="md-body-medium">
                    {t('Il pannello non può ricostruire sé stesso: la procedura, con i valori di questa installazione, va tenuta {fuori}. Le istanze intanto continuano a girare.', {
                      fuori: <b>{t('fuori di qui')}</b>,
                    })}
                  </p>
                  <Button variant="tonal" icon={<Copy />} onClick={copiaRunbook}>{t('Copia il runbook')}</Button>
                </Card>
              )}
            </div>
            {p?.runbook && (
              <Details summary={t('Il runbook «l’hub è perso», com’è adesso')}>
                <pre className="md-mono md-body-small" style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{p.runbook}</pre>
              </Details>
            )}
          </section>

          {/* --- Esercitazioni ------------------------------------------- */}
          <section className="md-stack">
            <div className="md-section-head">
              <h2 className="md-title-medium">{t('Esercitazioni ed esecuzioni')}</h2>
              <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>
                {t('Ogni sei mesi almeno una. La scheda dei tempi si compila da sola.')}
              </span>
            </div>
            {corse.isPending && <Skeleton height="6rem" />}
            {corse.isSuccess && (corse.data.runs ?? []).length === 0 && (
              <Banner icon={<Shield />} title={t('Nessuna corsa ancora')}>
                {t('«Il server è perso» in modalità di prova non tocca l’originale: le copie nascono con il suffisso -dr, si verificano, si tolgono. È il modo di sapere che il piano regge prima che serva.')}
              </Banner>
            )}
            {(corse.data?.runs ?? []).length > 0 && (
              <DataTable
                columns={[
                  { key: 'quando', header: t('Quando'), nowrap: true },
                  { key: 'cosa', header: t('Cosa') },
                  { key: 'esito', header: t('Esito') },
                  { key: 'rto', header: 'RTO', numeric: true },
                  { key: 'rpo', header: 'RPO', numeric: true },
                ]}
                rowKey={(r) => String(r.id)}
                onRowClick={(r) => navigate({ to: '/orgs/$slug/continuita/$id', params: { slug, id: String(r.id) } })}
                rows={(corse.data?.runs ?? []).map((c) => ({
                  id: c.id,
                  quando: quandoRelativo(c.startedAt),
                  cosa: (
                    <div className="md-due-righe">
                      <span className="md-body-large">{c.exercise ? t('Esercitazione') : t('Ripristino')} · {c.lostServerName} → {c.destServerName ?? '…'}</span>
                      <span>{t('{fatte} di {totale} istanze', { fatte: c.itemsDone, totale: c.itemsTotal })}{c.error ? ` · ${c.error}` : ''}</span>
                    </div>
                  ),
                  esito: <StatusChip status={c.status} label={t(ESITO_CORSA[c.status]?.label ?? c.status)} tone={ESITO_CORSA[c.status]?.tone ?? 'neutral'} />,
                  rto: <Misura valore={c.rtoMinutes} obiettivo={p?.targets.rtoMinutes} />,
                  rpo: <Misura valore={c.rpoMinutes} obiettivo={p?.targets.rpoMinutes} />,
                }))}
              />
            )}
          </section>
        </div>
      </div>

      {p && (
        <Obiettivi
          slug={slug}
          open={obiettivi}
          rto={p.targets.rtoMinutes}
          rpo={p.targets.rpoMinutes}
          onOpenChange={setObiettivi}
          onSalvato={() => qc.invalidateQueries({ queryKey: ['continuity', slug] })}
        />
      )}
      <ServerPerso
        slug={slug}
        open={disastro}
        onOpenChange={setDisastro}
        onAvviata={(run) => {
          qc.invalidateQueries({ queryKey: ['continuity-runs', slug] });
          qc.invalidateQueries({ queryKey: ['servers', slug] });
          qc.invalidateQueries({ queryKey: ['instances', slug] });
          navigate({ to: '/orgs/$slug/continuita/$id', params: { slug, id: run.id } });
        }}
      />
    </>
  );
}

/** Un minuto misurato contro l'obiettivo: verde entro, rosso oltre. */
function Misura({ valore, obiettivo }: { valore?: number; obiettivo?: number }) {
  if (valore === undefined) return <span className="md-body-small" style={{ color: 'var(--md-sys-color-on-surface-variant)' }}>—</span>;
  const oltre = obiettivo !== undefined && valore > obiettivo;
  return <span className="md-mono" style={{ color: oltre ? 'var(--md-sys-color-error)' : undefined }}>{ore(valore)}</span>;
}

/** Gli obiettivi: due numeri, in minuti, che sono una promessa al cliente. */
function Obiettivi({
  slug, open, rto, rpo, onOpenChange, onSalvato,
}: { slug: string; open: boolean; rto: number; rpo: number; onOpenChange: (v: boolean) => void; onSalvato: () => void }) {
  const permessi = usePermessi(slug);
  const avvisa = useSnackbar();
  const [r, setR] = useState(String(rto));
  const [p, setP] = useState(String(rpo));
  const salva = useMutation({
    mutationFn: () =>
      mustOk(api.PUT('/api/v1/orgs/{slug}/continuity/targets', {
        params: { path: { slug } }, body: { rtoMinutes: Number(r), rpoMinutes: Number(p) },
      })),
    onSuccess: () => {
      onSalvato();
      onOpenChange(false);
      avvisa(t('Obiettivi salvati: i semafori si misurano contro questi'));
    },
  });
  const ok = Number(r) >= 1 && Number(p) >= 1 && Number(r) <= 10080 && Number(p) <= 10080;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Obiettivi di ripristino')}
      description={t('Quello che si promette ai clienti di questa organizzazione. I semafori qui sopra si misurano contro questi numeri.')}
      actions={
        <>
          <Button variant="text" onClick={() => onOpenChange(false)}>{t('Annulla')}</Button>
          <ConPermesso ha={permessi} serve="org.manage">
            <Button variant="filled" icon={salva.isPending ? <Anello /> : undefined} disabled={!ok || salva.isPending} onClick={() => salva.mutate()}>{t('Salva')}</Button>
          </ConPermesso>
        </>
      }
    >
      <div className="md-form">
        {salva.error && <Banner tone="error" title={t('Non salvati')}>{(salva.error as Error).message}</Banner>}
        <TextField label={t('RTO — minuti per tornare su')} type="number" min={1} max={10080} value={r} onChange={(e) => setR(e.target.value)}
          help={t('Da quando si dichiara il disastro a quando l’ultima istanza risponde. 240 sono quattro ore.')} />
        <TextField label={t('RPO — minuti di dati che si possono perdere')} type="number" min={1} max={10080} value={p} onChange={(e) => setP(e.target.value)}
          help={t('L’età massima dell’ultimo backup. Con 60, ogni istanza vuole una pianificazione oraria.')} />
      </div>
    </Dialog>
  );
}

/**
 * La dichiarazione del disastro. Si sceglie il server perso e dove far
 * rinascere le sue istanze; si vede prima cosa succederà — quante istanze,
 * da quando — e si conferma scrivendo il nome, perché scollega un server.
 */
function ServerPerso({
  slug, open, onOpenChange, onAvviata,
}: { slug: string; open: boolean; onOpenChange: (v: boolean) => void; onAvviata: (run: Run) => void }) {
  const permessi = usePermessi(slug);
  const servers = useServers(slug);
  const istanze = useIstanze(slug);
  const credenziali = useCredenzialiCloud(slug);
  const [persoId, setPersoId] = useState('');
  const [destId, setDestId] = useState('');
  const [prova, setProva] = useState(false);
  const [conferma, setConferma] = useState('');
  const [nomeNuovo, setNomeNuovo] = useState('');
  const [macchina, setMacchina] = useState<MacchinaCloud>(MACCHINA_VUOTA);

  const tutti = servers.data?.servers ?? [];
  const pronti = tutti.filter((s) => s.status === 'ready');
  const perso = tutti.find((s) => s.id === persoId);
  // La destinazione: un server pronto che non sia quello perso — salvo in
  // prova, dove le copie possono stare accanto all'originale — oppure «uno
  // nuovo», se l'organizzazione ha un progetto OVH collegato: allora la corsa
  // lo crea e aspetta che sia pronto.
  const destinazioni = pronti.filter((s) => prova || s.id !== persoId);
  const puoCreare = (credenziali.data?.credentials ?? []).some((c) => c.provider === 'ovh');
  const NUOVO = '__nuovo__';
  const dest = destId === NUOVO && puoCreare ? NUOVO
    : destId && destinazioni.some((s) => s.id === destId) ? destId
    : destinazioni[0]?.id ?? (puoCreare ? NUOVO : '');
  const sue = (istanze.data?.instances ?? []).filter((i) => i.serverId === persoId && !i.staging);

  const avvia = useMutation({
    mutationFn: () =>
      must(api.POST('/api/v1/orgs/{slug}/continuity/server-lost', {
        params: { path: { slug } },
        body: dest === NUOVO
          // L'IP fisso non si sceglie qui: il server nuovo eredita quello del perso.
          ? { serverId: persoId, exercise: prova, newServer: { name: nomeNuovo, provider: 'ovh', credentialId: macchina.credentialId, region: macchina.region, size: macchina.size, monthlyBilling: macchina.monthlyBilling, providerBackups: macchina.providerBackups } }
          : { serverId: persoId, destinationServerId: dest, exercise: prova },
      })),
    onSuccess: (run) => {
      onOpenChange(false);
      setConferma('');
      onAvviata(run);
    },
  });

  const confermato = prova || (perso !== undefined && conferma.trim() === perso.name);
  const nuovoOk = dest !== NUOVO || (/^[a-z0-9][a-z0-9-]{1,62}$/.test(nomeNuovo) && !!macchina.credentialId && !!macchina.region && !!macchina.size);
  const pronto = !!persoId && !!dest && nuovoOk && confermato && !avvia.isPending;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={prova ? t('Esercitazione: il server è perso') : t('Il server è perso')}
      description={prova
        ? t('Come il disastro vero, senza toccare niente: le copie nascono con il suffisso -dr accanto alle istanze vere, si verificano e poi si tolgono a mano.')
        : t('Il server perso viene scollegato da Molo e le sue istanze rinascono dall’ultimo backup sul server che scegli, con lo stesso nome e lo stesso indirizzo.')}
      actions={
        <>
          <Button variant="text" onClick={() => onOpenChange(false)}>{t('Annulla')}</Button>
          {/* Un'esercitazione crea istanze accanto alle vere; il disastro vero
              scollega il server perso e ripristina sopra (dr/runs.go). */}
          <ConPermesso ha={permessi} serve={prova ? 'instance.create' : ['server.delete', 'db.restore']}>
            <Button variant={prova ? 'filled' : 'danger'} icon={avvia.isPending ? <Anello /> : undefined} disabled={!pronto} onClick={() => avvia.mutate()}>
              {avvia.isPending ? t('Avvio…') : prova ? t('Avvia l’esercitazione') : t('Dichiara il disastro')}
            </Button>
          </ConPermesso>
        </>
      }
    >
      <div className="md-form">
        {avvia.error && <Banner tone="error" title={t('Non avviata')}>{(avvia.error as Error).message}</Banner>}
        <Checkbox checked={prova} onCheckedChange={(v) => setProva(v === true)} label={t('È un’esercitazione: non toccare l’originale')} />
        <Select
          label={t('Server perso')}
          value={persoId}
          options={[{ value: '', label: t('Scegli…') }, ...tutti.map((s) => ({ value: s.id, label: `${s.name}${s.status !== 'ready' ? ` (${s.status})` : ''}` }))]}
          onChange={(e) => setPersoId(e.target.value)}
        />
        {persoId && (
          <Banner
            tone={sue.length ? 'info' : 'error'}
            title={!sue.length ? t('Nessuna istanza su questo server') : sue.length === 1 ? t('Un’istanza rinasce') : t('{n} istanze rinascono', { n: sue.length })}
          >
            {sue.length
              ? t('{nomi}. Ognuna dall’ultimo backup riuscito: un’istanza senza backup resta indietro, e la scheda lo dice.', { nomi: sue.map((i) => i.name).join(', ') })
              : t('Non c’è niente da far rinascere: scollegalo dalla sua scheda, se serve.')}
          </Banner>
        )}
        {destinazioni.length === 0 && !puoCreare ? (
          <Banner tone="error" title={t('Nessun server pronto su cui rinascere')}>
            {t('Collega un server nuovo (il one-liner di bootstrap: il provisioning va da solo in dieci minuti) e torna qui. Con un progetto OVH collegato in Impostazioni, da qui si crea da solo.')}
          </Banner>
        ) : (
          <Select
            label={t('Dove rinascono')}
            value={dest}
            options={[
              ...destinazioni.map((s) => ({ value: s.id, label: s.name })),
              ...(puoCreare ? [{ value: NUOVO, label: t('Un server nuovo su OVH, creato adesso') }] : []),
            ]}
            onChange={(e) => setDestId(e.target.value)}
            help={dest === NUOVO
              ? t('La corsa crea la macchina, aspetta i suoi passi di installazione (circa dieci minuti) e poi ripristina.')
              : t('Un server pronto dell’organizzazione. Il backup sta nella destinazione, non sulla macchina persa.')}
          />
        )}
        {dest === NUOVO && (
          <>
            <TextField
              label={t('Nome del server nuovo')}
              value={nomeNuovo}
              onChange={(e) => setNomeNuovo(e.target.value.toLowerCase())}
              placeholder={perso ? `${perso.name}-2` : 'srv-dr'}
              help={t('Solo minuscole, cifre e trattini. È anche il nome della macchina su OVH.')}
            />
            <ScegliMacchina slug={slug} value={macchina} onChange={setMacchina} senzaIP />
            {perso?.failoverIpv4 && (
              <Banner
                tone="info"
                title={prova
                  ? t('L’IP fisso {ip} resta dov’è', { ip: perso.failoverIpv4 })
                  : t('Eredita l’IP fisso {ip}', { ip: perso.failoverIpv4 })}
              >
                {prova
                  ? t('In esercitazione l’indirizzo della produzione non si sposta: la copia risponde sull’IP della macchina nuova.')
                  : t('Il server nuovo si porta dietro l’indirizzo: nessun DNS da aspettare. Scegli una regione della stessa zona (GRA, SBG e RBX stanno insieme).')}
              </Banner>
            )}
          </>
        )}
        {!prova && perso && (
          <TextField
            label={t('Scrivi il nome del server perso per confermare')}
            value={conferma}
            onChange={(e) => setConferma(e.target.value)}
            help={t('Scrivi «{nome}». Viene scollegato: Molo dimentica come entrarci e toglie i nomi delle sue istanze.', { nome: perso.name })}
          />
        )}
      </div>
    </Dialog>
  );
}

/** La scheda di una corsa: T0, T1, le istanze una per una, la scheda dei tempi. */
export function CorsaPage() {
  const { slug, id } = useParams({ from: '/shell/orgs/$slug/continuita/$id' });
  const navigate = useNavigate();
  const avvisa = useSnackbar();
  const q = useQuery({
    queryKey: ['continuity-run', slug, id],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/continuity/runs/{id}', { params: { path: { slug, id } } })),
    refetchInterval: (r) => (r.state.data?.status === 'running' ? 5000 : false),
  });
  const prontezza = useQuery({
    queryKey: ['continuity', slug],
    queryFn: () => must(api.GET('/api/v1/orgs/{slug}/continuity', { params: { path: { slug } } })),
  });
  const run = q.data;
  const ob = prontezza.data?.targets;

  const scheda = useMutation({
    mutationFn: () => must(api.GET('/api/v1/orgs/{slug}/continuity/runs/{id}/report', { params: { path: { slug, id } } })),
    onSuccess: (r) => {
      void navigator.clipboard?.writeText(r.markdown);
      avvisa(t('Scheda dei tempi copiata in Markdown'));
    },
  });

  return (
    <>
      <TopAppBar
        title={run ? `${run.exercise ? t('Esercitazione') : t('Ripristino')} · ${run.lostServerName} → ${run.destServerName ?? '…'}` : '…'}
        subtitle={run ? t('Dichiarato {quando}', { quando: new Date(run.startedAt).toLocaleString(localeIntl()) }) : undefined}
        actions={run && (
          <div className="md-stack__inline" style={{ alignItems: 'center' }}>
            <StatusChip status={run.status} label={t(ESITO_CORSA[run.status]?.label ?? run.status)} tone={ESITO_CORSA[run.status]?.tone ?? 'neutral'} />
            <Button variant="tonal" icon={<Copy />} disabled={scheda.isPending} onClick={() => scheda.mutate()}>{t('Copia la scheda')}</Button>
            <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/continuita', params: { slug } })}>{t('Continuità')}</Button>
          </div>
        )}
      />
      <CaricoSezione attiva={q.isPending} />
      <div className="md-content">
        {q.error && <Banner tone="error" title={t('Corsa non leggibile')}>{q.error.message}</Banner>}
        {run && (
          <div className="md-stack">
            {run.status === 'running' && (
              <Card variant="outlined">
                <div className="md-attesa">
                  <Logo size={56} anim="livello" label={t('In corso')} />
                  <p className="md-body-medium">
                    {t(FASE[run.phase] ?? run.phase)}.{' '}
                    {t('{fatte} di {totale} istanze rispondono.', { fatte: run.itemsDone, totale: run.itemsTotal })}
                  </p>
                </div>
              </Card>
            )}
            {run.status === 'failed' && (
              <Banner tone="error" icon={<Warn />} title={t('Non tutte le istanze sono tornate')}>{run.error}</Banner>
            )}
            {run.status === 'succeeded' && (
              <Banner tone="success" title={t('Tutte le istanze rispondono')}>
                {run.exercise
                  ? t('Verifica le copie -dr (login, conteggi, allegati), poi rimuovile dalla loro scheda.')
                  : t('I clienti ritrovano i loro indirizzi. Controlla i certificati dalle schede delle istanze.')}
              </Banner>
            )}
            <div className="md-metric-grid">
              <Card variant="outlined"><h3 className="md-title-small">{t('T0 · disastro dichiarato')}</h3><p className="md-body-large md-mono">{new Date(run.startedAt).toLocaleTimeString(localeIntl())}</p></Card>
              <Card variant="outlined"><h3 className="md-title-small">{t('T1 · ultima istanza risponde')}</h3><p className="md-body-large md-mono">{run.finishedAt ? new Date(run.finishedAt).toLocaleTimeString(localeIntl()) : '—'}</p></Card>
              <Card variant="outlined"><h3 className="md-title-small">{ob ? t('RTO misurato (obiettivo {obiettivo})', { obiettivo: ore(ob.rtoMinutes) }) : t('RTO misurato')}</h3><p className="md-body-large"><Misura valore={run.rtoMinutes} obiettivo={ob?.rtoMinutes} /></p></Card>
              <Card variant="outlined"><h3 className="md-title-small">{ob ? t('RPO misurato (obiettivo {obiettivo})', { obiettivo: ore(ob.rpoMinutes) }) : t('RPO misurato')}</h3><p className="md-body-large"><Misura valore={run.rpoMinutes} obiettivo={ob?.rpoMinutes} /></p></Card>
            </div>
            <DataTable
              columns={[
                { key: 'istanza', header: t('Istanza') },
                { key: 'snapshot', header: t('Dal backup del'), nowrap: true },
                { key: 'esito', header: t('Esito') },
                { key: 'azioni', header: '' },
              ]}
              rowKey={(r) => String(r.id)}
              rows={(run.items ?? []).map((i) => ({
                id: i.id,
                istanza: (
                  <div className="md-due-righe">
                    <span className="md-body-large">{i.sourceName}{i.newName !== i.sourceName ? ` → ${i.newName}` : ''}</span>
                    {i.error && <span>{i.error}</span>}
                  </div>
                ),
                snapshot: i.snapshotAt ? new Date(i.snapshotAt).toLocaleString(localeIntl(), { dateStyle: 'short', timeStyle: 'short' }) : '—',
                esito: <StatusChip status={i.status} label={t(ESITO_RIGA[i.status]?.label ?? i.status)} tone={ESITO_RIGA[i.status]?.tone ?? 'neutral'} />,
                azioni: (
                  <div className="md-stack__inline" style={{ justifyContent: 'flex-end' }}>
                    {i.jobId && <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/jobs/$id', params: { slug, id: i.jobId! } })}>{t('Log')}</Button>}
                    {i.newInstanceId && i.status === 'succeeded' && (
                      <Button variant="text" onClick={() => navigate({ to: '/orgs/$slug/instances/$id', params: { slug, id: i.newInstanceId! } })}>{t('Apri')}</Button>
                    )}
                  </div>
                ),
              }))}
            />
          </div>
        )}
      </div>
    </>
  );
}
